import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useAppSelector } from 'store/hook';
import {
  selectAssociatedFilteredAssets,
  selectAssetsLoaded,
} from 'store/asset.slice';
import { selectNotificationsData } from 'store/notification.slice';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import useAppTheme from 'hooks/useAppTheme';
import DeviceHandler from 'handlers/device.handler';
import { handleAssetStatus } from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { useControlRoomSSE } from 'hooks/useControlRoomSSE';
import { useControlRoomFuel } from 'hooks/useControlRoomFuel';
import { FleetLiveStatusPanel } from './panels/FleetLiveStatusPanel';
import { type MakeModelDetails } from 'types/makeModel.types';
import { timeAgo } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import {
  CRWrapper,
  CRHeader,
  HeaderLeft,
  PageTitle,
  PageTitleMain,
  PageTitleSub,
  HeaderRight,
  ShiftBadge,
  ShiftBadgeLabel,
  ShiftBadgeValue,
  LiveClock,
  ClockTime,
  ClockDate,
  CarouselOuter,
  ShiftBadgeSync,
  ShiftRow,
  KpiGrid,
  KpiCard,
  KpiLabel,
  KpiValue,
  KpiUnit,
  KpiSub,
} from './styles';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

const ControlRoom: React.FC = () => {
  const { t } = useTranslation();
  const [now, setNow] = useState(dayjs());
  const { enableDarkTheme } = useAppTheme();

  const { timezone: tz } = useTimeDisplayMode();

  /* ─── Redux selectors ─── */
  const associatedFilteredAssets = useAppSelector(
    selectAssociatedFilteredAssets
  );
  const latestAvailableData = useAppSelector(
    (state) => state.deviceReducer.latestOperationalData
  );
  const isLoadingOperationalData = useAppSelector(
    (state) => state.deviceReducer.isLoadingOperationalData
  );
  const isAppLoading = useAppSelector((state) => state.appReducer.isLoading);
  const notificationsData = useAppSelector(selectNotificationsData);
  const dtcData = useAppSelector((state) => state.dtcReducer);
  const dtc = Array.isArray(dtcData.allGroupedDTCData)
    ? dtcData.allGroupedDTCData.filter((item: any) => item.is_active)
    : [];
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer as MakeModelDetails[]
  );
  const unitSystem = useAppSelector(
    (state) => state.persistedReducer.customer.unitSystem
  );
  const customerName = useAppSelector(
    (state) => state.persistedReducer.customer.name
  );
  const assetsLoaded = useAppSelector(selectAssetsLoaded);
  const isProdLoading = useAppSelector(
    (state) => state.productionReducer.loadingStates.isLoadingFleetSummary
  );
  const isGeoLoading = useAppSelector((state) => state.geoReducer.isGeoLoading);

  const isDataLoading =
    !assetsLoaded ||
    isProdLoading ||
    isGeoLoading ||
    isLoadingOperationalData ||
    isAppLoading ||
    !latestAvailableData?.currentShift;

  /* ─── Live SSE for sensor-derived fields ─── */
  useControlRoomSSE();

  /* ─── Fuel snapshot on load, then kept live via SSE above ─── */
  useControlRoomFuel();

  /* ─── Fetch fresh sensor + operational data on mount ─── */
  const deviceHandler = new DeviceHandler();
  const latestSensorPollingId = useAppSelector(
    (state) => state.appReducer.latestSensorPollingId
  );

  const pollingIdRef = React.useRef<any>(null);
  useEffect(() => {
    pollingIdRef.current = latestSensorPollingId;
  }, [latestSensorPollingId]);

  useEffect(() => {
    // Start operational data polling (5 mins)
    void deviceHandler.startOperationalDataPolling(300000);

    return () => {
      if (pollingIdRef.current) {
        clearTimeout(pollingIdRef.current);
      }
    };
  }, []);

  /* ─── Live clock ─── */
  useEffect(() => {
    const id = setInterval(() => {
      setNow(dayjs());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  /* ─── Enriched assets ─── */
  const filteredAssets = useMemo(
    () =>
      addLiveDataToAssets(
        associatedFilteredAssets,
        {}, // No more raw tag data dependencies
        notificationsData,
        undefined,
        dtc
      ),
    [associatedFilteredAssets, notificationsData, dtc]
  );

  /* ─── Current shift (from available data API) ─── */
  const currentShift = useMemo(() => {
    return (latestAvailableData?.currentShift as unknown as string) || null;
  }, [latestAvailableData]);

  const activeAssets = filteredAssets.filter(
    (a) => handleAssetStatus(a.isActive) === 'available'
  ).length;

  const overallLastSync = useMemo(() => {
    let maxTs = 0;
    // Walk the available data snapshot to find latest update
    for (const deviceId in latestAvailableData) {
      const dev = latestAvailableData[deviceId];
      if (dev && typeof dev === 'object' && dev.latestTs > maxTs) {
        maxTs = dev.latestTs;
      }
    }
    return maxTs > 0 ? timeAgo(maxTs) : 'N/A';
  }, [latestAvailableData]);

  /* ─── Clock display ─── */
  const localNow = tz ? now.tz(tz) : now;
  const timeStr = localNow.format('hh:mm:ss A');
  const dateStr = localNow.format('ddd, DD MMM YYYY');

  return (
    <CRWrapper $enableDarkTheme={enableDarkTheme}>
      {/* ── Header ── */}
      <CRHeader $enableDarkTheme={enableDarkTheme}>
        <HeaderLeft>
          <PageTitle>
            <PageTitleMain>{t('control_room')}</PageTitleMain>
            <PageTitleSub>{customerName || 'Fleet Operations'}</PageTitleSub>
          </PageTitle>
        </HeaderLeft>

        <HeaderRight>
          {currentShift && (
            <ShiftBadge $enableDarkTheme={enableDarkTheme}>
              <ShiftRow>
                <ShiftBadgeLabel $enableDarkTheme={enableDarkTheme}>
                  {t('current_shift')}
                </ShiftBadgeLabel>
                <ShiftBadgeValue>{currentShift}</ShiftBadgeValue>
              </ShiftRow>
              <ShiftBadgeSync $enableDarkTheme={enableDarkTheme}>
                {t('last_sync')} {overallLastSync}
              </ShiftBadgeSync>
            </ShiftBadge>
          )}
          <LiveClock>
            <ClockTime $enableDarkTheme={enableDarkTheme}>{timeStr}</ClockTime>
            <ClockDate $enableDarkTheme={enableDarkTheme}>{dateStr}</ClockDate>
          </LiveClock>
        </HeaderRight>
      </CRHeader>

      {/* ── Main Panel ── */}
      <CarouselOuter>
        <div style={{ padding: '18px 24px 72px' }}>
          {/* KPI Summary Cards */}
          {!isDataLoading && (
            <KpiGrid>
              {/* 1 — Equipment Utilization */}
              <KpiCard $enableDarkTheme={enableDarkTheme}>
                <KpiLabel $enableDarkTheme={enableDarkTheme}>
                  {t('equipment_utilization')}
                </KpiLabel>
                <KpiValue $accent $enableDarkTheme={enableDarkTheme}>
                  {latestAvailableData?.summary?.equipmentUtilization ? (
                    <>
                      {latestAvailableData.summary.equipmentUtilization.toFixed(
                        0
                      )}
                      <KpiUnit>%</KpiUnit>
                    </>
                  ) : (
                    '—'
                  )}
                </KpiValue>
                <KpiSub $enableDarkTheme={enableDarkTheme}>
                  {t('productive_hours_vs_engine_hours')}
                </KpiSub>
              </KpiCard>

              {/* 2 — Shift Tonnage */}
              <KpiCard $enableDarkTheme={enableDarkTheme}>
                <KpiLabel $enableDarkTheme={enableDarkTheme}>
                  {t('shift_tonnage_t')}
                </KpiLabel>
                <KpiValue $enableDarkTheme={enableDarkTheme}>
                  {latestAvailableData?.summary?.totalTonnage ? (
                    <>
                      {latestAvailableData.summary.totalTonnage.toLocaleString(
                        'en-US',
                        {
                          maximumFractionDigits: 0,
                        }
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </KpiValue>
                <KpiSub $enableDarkTheme={enableDarkTheme}>
                  {t('from_production_records')}
                </KpiSub>
              </KpiCard>

              {/* 3 — Fleet Trips Completed */}
              <KpiCard $enableDarkTheme={enableDarkTheme}>
                <KpiLabel $enableDarkTheme={enableDarkTheme}>
                  {t('fleet_trips_completed')}
                </KpiLabel>
                <KpiValue $accent $enableDarkTheme={enableDarkTheme}>
                  {latestAvailableData?.summary?.totalTrips &&
                  latestAvailableData.summary.totalTrips > 0
                    ? latestAvailableData.summary.totalTrips
                    : '—'}
                </KpiValue>
                <KpiSub $enableDarkTheme={enableDarkTheme}>
                  {t('today_across_all_trucks')}
                </KpiSub>
              </KpiCard>

              {/* 4 — Avg Cycle Time */}
              <KpiCard $enableDarkTheme={enableDarkTheme}>
                <KpiLabel $enableDarkTheme={enableDarkTheme}>
                  {t('avg_cycle_time_min')}
                </KpiLabel>
                <KpiValue $enableDarkTheme={enableDarkTheme}>
                  {latestAvailableData?.summary?.avgCycleMin &&
                  Number(latestAvailableData.summary.avgCycleMin) > 0
                    ? latestAvailableData.summary.avgCycleMin
                    : '—'}
                </KpiValue>
                <KpiSub $enableDarkTheme={enableDarkTheme}>
                  {t('from_production_records')}
                </KpiSub>
              </KpiCard>

              {/* 5 — Active / Available Trucks */}
              <KpiCard $enableDarkTheme={enableDarkTheme}>
                <KpiLabel $enableDarkTheme={enableDarkTheme}>
                  {t('active_available_trucks')}
                </KpiLabel>
                <KpiValue $accent $enableDarkTheme={enableDarkTheme}>
                  {(() => {
                    const onlineCount = filteredAssets.filter((a) => {
                      const deviceId = a.device?.deviceId;
                      if (!deviceId) return false;
                      const opDataForAsset = latestAvailableData[deviceId];
                      return opDataForAsset?.ignitionOn === true;
                    }).length;
                    return `${onlineCount} / ${activeAssets}`;
                  })()}
                </KpiValue>
                <KpiSub $enableDarkTheme={enableDarkTheme}>
                  {t('currently_ignition_on')}
                </KpiSub>
              </KpiCard>
            </KpiGrid>
          )}

          {/* Fleet Live Status Panel */}
          {!isDataLoading ? (
            <FleetLiveStatusPanel
              filteredAssets={filteredAssets}
              latestAvailableData={latestAvailableData}
              unitSystem={unitSystem}
              enableDarkTheme={enableDarkTheme}
              assetsLoaded={assetsLoaded}
              startTs={latestAvailableData?.startTs}
              endTs={latestAvailableData?.endTs}
              makeModelDetails={makeModelDetails}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: '400px',
              }}
            >
              <SingleFadeLoader isFullScreen={true} />
            </Box>
          )}
        </div>
      </CarouselOuter>
    </CRWrapper>
  );
};

export default ControlRoom;
